import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    getNotifications,
    getUnreadNotificationCount,
    markAllNotificationsRead,
    markNotificationRead,
    type UserNotification,
} from '../services/notificationService'
import { formatPlatformDateTime } from '../utils/platformDate'
import { FiBell } from 'react-icons/fi'

export default function NotificationsPopover() {
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const [notifications, setNotifications] = useState<UserNotification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(false)

    const refresh = useCallback(async () => {
        try {
            const [count, page] = await Promise.all([
                getUnreadNotificationCount(),
                getNotifications(0, 10),
            ])
            setUnreadCount(count)
            const items = Array.isArray(page) ? page : (page?.content ?? [])
            setNotifications(items)
        } catch {
            setUnreadCount(0)
            setNotifications([])
        }
    }, [])

    useEffect(() => {
        const loadCount = () => {
            getUnreadNotificationCount()
                .then(setUnreadCount)
                .catch(() => setUnreadCount(0))
        }
        loadCount()
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                loadCount()
            }
        }, 30_000)
        return () => window.clearInterval(timer)
    }, [])

    const toggleOpen = async () => {
        const next = !open
        setOpen(next)
        if (next) {
            setLoading(true)
            await refresh()
            setLoading(false)
        }
    }

    const handleClick = async (notification: UserNotification) => {
        if (!notification.read) {
            await markNotificationRead(notification.id)
        }
        setOpen(false)
        if (notification.linkPath) {
            const isChat = notification.linkPath.includes('/messages')
            navigate(notification.linkPath, {
                state: isChat ? { chatRefresh: Date.now() } : undefined,
            })
        }
        refresh()
    }

    const handleMarkAllRead = async () => {
        await markAllNotificationsRead()
        refresh()
    }

    return (
        <div className="cl-dropdown-wrap">
            <button
                id="cl-notif-btn"
                type="button"
                className="cl-icon-btn"
                onClick={toggleOpen}
                title="Notifications"
            >
                <FiBell size={18} aria-hidden="true" />
                {unreadCount > 0 && <span className="cl-notif-dot" />}
            </button>
            {open && (
                <div className="cl-dropdown cl-notif-dropdown">
                    <div className="cl-dropdown-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Notifications</span>
                        {unreadCount > 0 && (
                            <button type="button" className="cl-chat-see-all" onClick={handleMarkAllRead}>
                                Mark all read
                            </button>
                        )}
                    </div>
                    {loading ? (
                        <div className="cl-notif-empty">
                            <p>Loading...</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="cl-notif-empty">
                            <FiBell size={32} aria-hidden="true" />
                            <p>No new notifications</p>
                        </div>
                    ) : (
                        <div>
                            {notifications.map((notification) => (
                                <button
                                    key={notification.id}
                                    type="button"
                                    className={`cl-chat-item ${notification.read ? '' : 'cl-chat-unread'}`}
                                    onClick={() => handleClick(notification)}
                                    style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer' }}
                                >
                                    <div className="cl-chat-body">
                                        <div className="cl-chat-name-row">
                                            <span className="cl-chat-name">{notification.title}</span>
                                        </div>
                                        <p className="cl-chat-msg">{notification.message}</p>
                                        <span className="cl-chat-time">
                                            {formatPlatformDateTime(notification.createdAt)}
                                        </span>
                                    </div>
                                    {!notification.read && <span className="cl-chat-badge" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
